import React, { memo, useMemo } from 'react';
import { Grid, Typography, Box, Card, CardActionArea, CardContent } from '@material-ui/core';
import { useRouteMatch } from 'react-router';
import { Link } from 'react-router-dom';
import Icon from '@mdi/react';
import { SupportCategory } from '../__generated__/SupportCategory';
import { iconList } from '../../../../common/icons/RoleIconList';

interface GridViewLandingProps {
  categories: SupportCategory[];
}

const GridViewLanding = memo((props: GridViewLandingProps) => {
  const { categories } = props;
  const match = useRouteMatch();

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.title < b.title ? -1 : 1)),
    [categories]
  );

  return (
    <Grid container spacing={3}>
      {sortedCategories.map((category) => {
        const categoryColour = (category as any).colour;
        const categoryIcon = (category as any).icon;
        const resourceCount = category.supportResources.length;

        return (
          <Grid item xs={12} sm={6} md={4} key={category.supportCategoryId}>
            <Link
              to={match?.url ? `${match.url}/${category.supportCategoryId}` : ''}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <Card
                style={{
                  borderRadius: 12,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  borderTop: categoryColour ? `4px solid ${categoryColour}` : '4px solid #7047EB',
                  height: '100%',
                  transition: 'box-shadow 0.2s, transform 0.2s',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 16px rgba(0,0,0,0.14)';
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                }}
              >
                <CardActionArea style={{ height: '100%' }}>
                  <CardContent style={{ padding: 24 }}>
                    <Box display='flex' alignItems='center' mb={2}>
                      {categoryIcon && iconList[categoryIcon] ? (
                        <Box
                          display='flex'
                          alignItems='center'
                          justifyContent='center'
                          mr={1.5}
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 8,
                            backgroundColor: categoryColour ? `${categoryColour}18` : '#7047EB18',
                          }}
                        >
                          <Icon path={iconList[categoryIcon]} size={1} color={categoryColour || '#7047EB'} />
                        </Box>
                      ) : null}
                      <Typography variant='h6' style={{ fontWeight: 600 }}>
                        {category.title}
                      </Typography>
                    </Box>

                    <Typography variant='body2' color='textSecondary' style={{ marginBottom: 16 }}>
                      {resourceCount === 0
                        ? 'No resources yet'
                        : `${resourceCount} ${resourceCount === 1 ? 'resource' : 'resources'}`}
                    </Typography>

                    {category.supportResources.slice(0, 3).map((resource) => (
                      <Typography
                        key={resource.supportResourceId}
                        variant='body2'
                        noWrap
                        style={{
                          padding: '4px 0',
                          borderBottom: '1px solid #f0f0f0',
                          color: '#555',
                        }}
                      >
                        {resource.title}
                      </Typography>
                    ))}
                    {resourceCount > 3 ? (
                      <Typography variant='body2' color='primary' style={{ marginTop: 8, fontWeight: 500 }}>
                        {`+ ${resourceCount - 3} more`}
                      </Typography>
                    ) : null}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Link>
          </Grid>
        );
      })}
    </Grid>
  );
});

export { GridViewLanding };
